import { GDTF_REGEX } from "@coten-inc/utils/dist/gdtf/parser";
import type { GeoJSON } from "geojson";
import { Info, MapPin } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { GdtfDateInput } from "~/components/gdtf-date-input";
import { SectionCard } from "~/components/section-card";
import { Button, Input, Label, Slider, Tabs, TabsList, TabsTrigger, Textarea } from "~/components/ui";
import type {
  GeoItemCreateAreaInputMode,
  GeoItemCreateDraftData,
  GeoItemCreateFormData,
} from "~/features/geoItems/types";
import { parseGeoItemCreateDraftGeometry } from "~/features/geoItems/utils/geoItemCreateDraft";
import {
  EditMap,
  type EditMapHandles,
  EditMapTools,
  RouteEditorActions,
  RouteEditorMap,
  useEditMapTools,
} from "~/features/recordGeoItems";
import { canEditRouteGeoJson } from "~/features/recordGeoItems/utils/routeEditorGeoJson";
import type { ViewportType } from "~/lib/map/types";
import { resolveGdtfJdnRange } from "~/utils/gdtfRange";

const MAX_RESOLUTION = 4;

interface GeoItemCreateFormProps {
  defaultValues: GeoItemCreateFormData;
  initialDraft?: GeoItemCreateDraftData;
  processing: boolean;
  onSubmit: (data: GeoItemCreateFormData) => boolean | Promise<boolean>;
  onCancel: () => void;
  onDraftChange?: (data: GeoItemCreateDraftData, hasUnsavedChanges: boolean) => void;
  onTemporarySave?: (data: GeoItemCreateDraftData) => boolean;
}

const resolveAreaInputMode = (form: GeoItemCreateFormData): GeoItemCreateAreaInputMode =>
  form.h3Indexes.length > 0 || !form.geometry ? "h3" : "geoJson";

const createInitialDraft = (form: GeoItemCreateFormData): GeoItemCreateDraftData => ({
  form,
  areaInputMode: resolveAreaInputMode(form),
  resolution: 3,
  geoJsonText: form.geometry ? JSON.stringify(form.geometry, null, 2) : "",
  h3IndexesDraft: form.h3Indexes,
  geometryDraft: form.geometry,
});

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areGeoJsonEqual = (left: GeoJSON | null, right: GeoJSON | null): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const isSameDraft = (left: GeoItemCreateDraftData, right: GeoItemCreateDraftData): boolean =>
  left.areaInputMode === right.areaInputMode &&
  left.resolution === right.resolution &&
  left.geoJsonText === right.geoJsonText &&
  areStringArraysEqual(left.h3IndexesDraft, right.h3IndexesDraft) &&
  left.form.startDate === right.form.startDate &&
  left.form.endDate === right.form.endDate &&
  left.form.jaLabel === right.form.jaLabel &&
  left.form.enLabel === right.form.enLabel &&
  left.form.memo === right.form.memo &&
  areGeoJsonEqual(left.form.geometry, right.form.geometry) &&
  areGeoJsonEqual(left.geometryDraft, right.geometryDraft) &&
  areStringArraysEqual(left.form.h3Indexes, right.form.h3Indexes);

export function GeoItemCreateForm({
  defaultValues,
  initialDraft,
  processing,
  onSubmit,
  onCancel,
  onDraftChange,
  onTemporarySave,
}: GeoItemCreateFormProps) {
  const { t } = useTranslation();
  const initialDraftRef = useRef(initialDraft ?? createInitialDraft(defaultValues));
  const cleanDraftRef = useRef(initialDraftRef.current);
  const [, setCleanRevision] = useState(0);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<GeoItemCreateFormData>({ defaultValues: initialDraftRef.current.form });

  const {
    comparisonAreas,
    pinnedLocations,
    setPinnedLocations,
    searchedLocation,
    setSearchedLocation,
    inViewportLocations,
    setViewport,
  } = useEditMapTools();
  const editMapRef = useRef<EditMapHandles>(null);

  const initialAreaInputMode = initialDraftRef.current.areaInputMode;
  const [resolution, setResolution] = useState<GeoItemCreateDraftData["resolution"]>(
    initialDraftRef.current.resolution,
  );
  const [routeEditorOpen, setRouteEditorOpen] = useState(
    initialAreaInputMode === "geoJson" &&
      (!initialDraftRef.current.geoJsonText.trim() ||
        (initialDraftRef.current.geometryDraft !== null && canEditRouteGeoJson(initialDraftRef.current.geometryDraft))),
  );
  const [areaInputMode, setAreaInputMode] = useState<GeoItemCreateAreaInputMode>(initialAreaInputMode);
  const h3IndexesDraftRef = useRef(initialDraftRef.current.h3IndexesDraft);
  const [geometryDraft, setGeometryDraft] = useState(initialDraftRef.current.geometryDraft);
  const [geoJsonText, setGeoJsonText] = useState(initialDraftRef.current.geoJsonText);

  // `useWatch` (hook) instead of the `watch()` method: the method can't be memoized,
  // so React Compiler would skip memoizing this component and the values passed to <EditMap>.
  const [startDate, endDate, jaLabel, enLabel, h3Indexes, geometry, memo] = useWatch({
    control,
    name: ["startDate", "endDate", "jaLabel", "enLabel", "h3Indexes", "geometry", "memo"],
  });
  const draftData = useMemo<GeoItemCreateDraftData>(
    () => ({
      form: { startDate, endDate, jaLabel, enLabel, h3Indexes, geometry, memo },
      areaInputMode,
      resolution,
      geoJsonText,
      h3IndexesDraft: h3IndexesDraftRef.current,
      geometryDraft,
    }),
    [
      areaInputMode,
      enLabel,
      endDate,
      geoJsonText,
      geometry,
      geometryDraft,
      h3Indexes,
      jaLabel,
      memo,
      resolution,
      startDate,
    ],
  );
  const hasUnsavedChanges = !isSameDraft(draftData, cleanDraftRef.current);

  useEffect(() => {
    onDraftChange?.(draftData, hasUnsavedChanges);
  }, [draftData, hasUnsavedChanges, onDraftChange]);

  const toggleHexagon = (h3Index: string) => {
    const current = getValues("h3Indexes");
    const next = current.includes(h3Index) ? current.filter((i) => i !== h3Index) : [...current, h3Index];
    h3IndexesDraftRef.current = next;
    setValue("h3Indexes", next);
    clearErrors("geometry");
  };

  const onGeoJsonChange = (text: string) => {
    setGeoJsonText(text);
    if (!text.trim()) {
      setGeometryDraft(null);
      setValue("geometry", null);
      clearErrors("geometry");
      return;
    }
    const next = parseGeoItemCreateDraftGeometry(text);
    if (!next) {
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.invalidGeoJson") });
      return;
    }
    setGeometryDraft(next);
    setValue("geometry", next);
    setValue("h3Indexes", []);
    clearErrors("geometry");
  };

  const changeAreaInputMode = (nextMode: GeoItemCreateAreaInputMode) => {
    if (nextMode === areaInputMode) return;

    if (areaInputMode === "h3") {
      h3IndexesDraftRef.current = getValues("h3Indexes");
    } else {
      setGeometryDraft(getValues("geometry"));
    }

    setAreaInputMode(nextMode);
    if (nextMode === "h3") {
      setRouteEditorOpen(false);
      setValue("h3Indexes", h3IndexesDraftRef.current);
      setValue("geometry", null);
      clearErrors("geometry");
      return;
    }

    setValue("h3Indexes", []);
    if (!geoJsonText.trim()) {
      setValue("geometry", null);
      clearErrors("geometry");
      setRouteEditorOpen(true);
    } else if (!parseGeoItemCreateDraftGeometry(geoJsonText)) {
      setValue("geometry", null);
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.invalidGeoJson") });
      setRouteEditorOpen(false);
    } else {
      setValue("geometry", geometryDraft);
      clearErrors("geometry");
      setRouteEditorOpen(canEditRouteGeoJson(geometryDraft));
    }
  };

  const onRouteEditorChange = (next: GeoJSON) => {
    setGeometryDraft(next);
    setValue("geometry", next);
    setGeoJsonText(JSON.stringify(next, null, 2));
    clearErrors("geometry");
  };

  const submit = async (data: GeoItemCreateFormData) => {
    let valid = true;
    const { fromJdn, toJdn } = resolveGdtfJdnRange(data.startDate || undefined, data.endDate || undefined);
    if (fromJdn !== null && toJdn !== null && fromJdn > toJdn) {
      setError("endDate", { message: t("feature.recordGeoItems.form.errors.invertedGdtfRange") });
      valid = false;
    }
    if (!data.jaLabel && !data.enLabel) {
      setError("jaLabel", { message: t("ui.required") });
      valid = false;
    }
    if (data.h3Indexes.length === 0 && !data.geometry) {
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.eitherGeoJsonOrH3IndexesRequired") });
      valid = false;
    }
    if (areaInputMode === "geoJson" && geoJsonText.trim() && !parseGeoItemCreateDraftGeometry(geoJsonText)) {
      setError("geometry", { message: t("feature.recordGeoItems.form.errors.invalidGeoJson") });
      valid = false;
    }
    if (errors.geometry) valid = false;
    if (!valid) return;

    if (await onSubmit(data)) {
      cleanDraftRef.current = { ...draftData, form: data };
      setCleanRevision((revision) => revision + 1);
    }
  };

  const temporarilySave = () => {
    if (!onTemporarySave?.(draftData)) return;
    cleanDraftRef.current = draftData;
    setCleanRevision((revision) => revision + 1);
  };

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-5">
      <SectionCard icon={Info} title={t("ui.detail")} contentClassName="grid gap-4 sm:grid-cols-2">
        <Field label={t("model.recordGeoItem.i18nLabel.ja")} error={errors.jaLabel?.message}>
          <Input {...register("jaLabel")} />
        </Field>
        <Field label={t("model.recordGeoItem.i18nLabel.en")} error={errors.enLabel?.message}>
          <Input {...register("enLabel")} />
        </Field>
        <Controller
          control={control}
          name="startDate"
          rules={{
            validate: (value) =>
              !value || GDTF_REGEX.test(value) || t("feature.recordGeoItems.form.errors.invalidGdtfFormat"),
          }}
          render={({ field, fieldState }) => (
            <GdtfDateInput
              label={t("model.geoItem.startDate")}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="endDate"
          rules={{
            validate: (value) =>
              !value || GDTF_REGEX.test(value) || t("feature.recordGeoItems.form.errors.invalidGdtfFormat"),
          }}
          render={({ field, fieldState }) => (
            <GdtfDateInput
              label={t("model.geoItem.endDate")}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Field label={t("model.recordGeoItem.memo")} className="sm:col-span-2">
          <Textarea {...register("memo")} className="min-h-[4rem]" />
        </Field>
      </SectionCard>

      <SectionCard
        icon={MapPin}
        title={t("feature.recordGeoItems.form.area")}
        contentClassName="space-y-4"
        action={
          areaInputMode === "h3" ? (
            <div className="flex items-center gap-3">
              <Label className="whitespace-nowrap text-12-bold text-fg-2">
                {t("feature.recordGeoItems.form.resolution")}
              </Label>
              <Slider
                min={0}
                max={MAX_RESOLUTION}
                step={1}
                value={resolution}
                onChange={(e) => setResolution(Number(e.target.value) as GeoItemCreateDraftData["resolution"])}
                className="w-40"
                aria-label={t("feature.recordGeoItems.form.resolution")}
              />
              <span className="w-3 text-12-bold tabular-nums text-fg-1">{resolution}</span>
            </div>
          ) : undefined
        }
      >
        <Tabs value={areaInputMode} onValueChange={(value) => changeAreaInputMode(value as GeoItemCreateAreaInputMode)}>
          <TabsList>
            <TabsTrigger value="h3">{t("feature.recordGeoItems.form.areaInputMode.h3")}</TabsTrigger>
            <TabsTrigger value="geoJson">{t("feature.recordGeoItems.form.areaInputMode.geoJson")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {areaInputMode === "geoJson" && !routeEditorOpen && (
          <RouteEditorActions geometry={geometry} onStart={() => setRouteEditorOpen(true)} />
        )}
        {routeEditorOpen ? (
          <RouteEditorMap
            geometry={geometry}
            onChange={onRouteEditorChange}
            onClose={() => setRouteEditorOpen(false)}
          />
        ) : (
          <EditMap
            ref={editMapRef}
            resolution={resolution}
            areaH3Indexes={h3Indexes}
            geometry={geometry}
            enableHexagonSelection={areaInputMode === "h3"}
            onHexagonClick={toggleHexagon}
            comparisonAreas={comparisonAreas}
            pinnedLocations={pinnedLocations}
            searchedLocation={searchedLocation}
            inViewportLocations={inViewportLocations}
            onLocationClick={(location) => {
              setSearchedLocation(null);
              setPinnedLocations([...pinnedLocations, { ...location, isShow: true }]);
            }}
            onViewPortChange={(vp) => setViewport(vp as ViewportType)}
            tools={<EditMapTools onMoveTo={(coords) => editMapRef.current?.moveTo(coords)} />}
          />
        )}
        {areaInputMode === "geoJson" && (
          <Field label={t("model.recordGeoItem.geoJson")} error={errors.geometry?.message}>
            <Textarea
              value={geoJsonText}
              onChange={(e) => onGeoJsonChange(e.target.value)}
              disabled={routeEditorOpen}
              spellCheck={false}
              className="min-h-[6rem] font-mono text-[13px]"
            />
          </Field>
        )}
      </SectionCard>

      <div className="flex items-center justify-between border-t border-border-default pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={processing}>
          {t("ui.cancel")}
        </Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={temporarilySave} disabled={processing}>
            {t("feature.geoItems.create.draft.temporarySave")}
          </Button>
          <Button type="submit" disabled={processing}>
            {t("ui.create")}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  className,
  children,
}: {
  label: string;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <Label className="text-12-bold capitalize text-fg-2">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-12-medium text-danger">{error}</p>}
    </div>
  );
}
